import React, { useState, useEffect, useCallback, useMemo, memo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import toast from 'common/utils/toast'
import { useTranslation } from 'react-i18next'
import Calendar from '../Calendar'
import Grid from '@material-ui/core/Grid'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Divider from '@material-ui/core/Divider'
import {
  StyledAppBar,
  StyledDrawer,
  StyledUserName,
  StyledTradeMarkContainer,
  StyledToolbar,
  StyledTradeMarkBox
} from './styles'
import { useStore } from 'components/Store'
import ItemsList from './components/ItemsList'
import { getRoutes } from './help/routes'
import { filterRoutesByPermissions } from 'common/utils/menu'
import { routesPermissions } from 'routes'
import { ReactComponent as LogoGPCNew } from 'assets/images/logo-gpc-new.svg'
import { ReactComponent as UserIconNew } from 'assets/images/ic-user-new.svg'
import { ReactComponent as ArrowDownIcon } from 'assets/images/ic-arrow-down-white.svg'
import { ReactComponent as CalendarIconNew } from 'assets/images/ic-calendar-new.svg'
import { ReactComponent as ChatIcon } from 'assets/images/ic-user-menu-chat.svg'
import { ReactComponent as PasswordIcon } from 'assets/images/ic-user-menu-password.svg'
import { ReactComponent as UserEditionIcon } from 'assets/images/ic-user-menu-edition.svg'
import { ReactComponent as FilesIcon } from 'assets/images/ic-user-menu-files.svg'
import { ReactComponent as ExitIcon } from 'assets/images/ic-user-menu-exit.svg'
import ChangePasswordModal from './components/modals/ChangePasswordModal'
import RequestUserEditionModal from './components/modals/RequestUserEditionModal'
import _get from 'lodash/get'

function MiniDrawer () {
  const [menuItems, setMenuItems] = useState([])
  const [clickedOnLogout, setClickedOnLogout] = useState(false)
  const [isCalendarOpen, setCalendarOpen] = useState(false)
  const [calendarDismissed, setCalendarDismissed] = useState(() => window.sessionStorage.getItem('calendarDismissed') === 'true')
  const [anchorEl, setAnchorEl] = useState(null)
  const [menuState, setMenuState] = useState({
    opened: false,
    blocked: false
  })
  const [modalChangePassword, setModalChangePassword] = useState({
    openModal: false,
    data: {}
  })
  const [modalRequestUserEdition, setModalRequestUserEdition] = useState({
    openModal: false,
    data: {}
  })

  const location = useLocation()
  const history = useHistory()

  const { store, setStore } = useStore('AUTH')

  const { t } = useTranslation()

  const disableMenu = location.state && location.state.fullWidth

  const openModalChangePassword = useCallback(() => {
    setModalChangePassword(prevState => ({
      ...prevState,
      openModal: true
    }))
  }, [])

  const openRequestUserEditionModal = useCallback(() => {
    setModalRequestUserEdition(prevState => ({
      ...prevState,
      openModal: true
    }))
  }, [])

  const closeAllMenus = useCallback(() => {
    window.dispatchEvent(new window.Event('gpc-close-sidepanels'))
    setMenuState({ blocked: false, opened: false })
  }, [])

  const closeMenu = useCallback(() => {
    setMenuState({ blocked: true, opened: false })
  }, [])

  const handleToggleMenu = useCallback((open) => {
    if (open) {
      if (!menuState.blocked) {
        setMenuState(currentState => ({ ...currentState, opened: true }))
      }
    } else {
      window.dispatchEvent(new window.Event('gpc-close-sidepanels'))
      setMenuState({ blocked: false, opened: false })
    }
  }, [menuState.blocked])

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleUserMenuClose = () => {
    setAnchorEl(null)
  }

  const handleDiretoryDrive = () => {
    window.open('https://drive.google.com/drive/folders/1JTkp7tXZ8LKbDynJI1rdCa6phivekjLt', '_blank')
    handleUserMenuClose()
  }

  const mouseOver = () => {
    if (!menuState.opened) {
      handleToggleMenu(true)
    }
  }

  const handleCalendarOpenChange = useCallback((open) => {
    setCalendarOpen(open)
    if (!open && location.pathname === '/') {
      setCalendarDismissed(true)
      window.sessionStorage.setItem('calendarDismissed', 'true')
    }
  }, [location.pathname])

  useEffect(() => {
    if (location.pathname === '/') {
      if (!calendarDismissed) {
        setCalendarOpen(true)
      }
    } else {
      setCalendarOpen(false)
    }
  }, [calendarDismissed, location.pathname])

  const clickAway = (event) => {
    const target = event?.target
    if (target?.closest?.('[data-gpc-sidepanel="true"]')) return
    if (target?.closest?.('.gpc-sidepanel-popover-paper')) return

    closeAllMenus()
  }

  useEffect(() => {
    const handler = (event) => {
      const target = event?.target
      if (target?.closest?.('.MuiDrawer-paper')) return
      if (target?.closest?.('[data-gpc-sidepanel="true"]')) return
      if (target?.closest?.('.MuiMenu-paper')) return
      closeAllMenus()
    }

    document.addEventListener('mousedown', handler, true)
    return () => document.removeEventListener('mousedown', handler, true)
  }, [closeAllMenus])

  const sessionExpiredRedirect = useCallback(() => {
    if (location.pathname !== '/login' && !clickedOnLogout) {
      toast.warn(t('general.expiredToken'))
      history.push('/login')
    }
  }, [clickedOnLogout, history, location, t])

  const logout = useCallback(() => {
    window.localStorage.clear()
    window.sessionStorage.clear()
    window.localStorage.setItem('expiredToken', true)
    setClickedOnLogout(true)
    setStore({ access_token: '', isLogged: false }, () => {
      history.push('/login')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history])

  useEffect(() => {
    const interval = setInterval(() => {
      const accessToken = window.localStorage.getItem('access_token')
      const expiredToken = window.localStorage.getItem('expiredToken')
      if (!accessToken || (expiredToken === 'true' && location.pathname !== '/login')) {
        clearInterval(interval)
        sessionExpiredRedirect()
        setClickedOnLogout(false)
      }
    }, 4000)
    return () => clearInterval(interval)
  }, [sessionExpiredRedirect, history, location, t])

  useEffect(() => {
    const routes = getRoutes(closeMenu, logout, openModalChangePassword, openRequestUserEditionModal)
    setMenuItems(filterRoutesByPermissions(routes, routesPermissions, store.access_token))
  }, [closeMenu, history, logout, openModalChangePassword, openRequestUserEditionModal, store.access_token])

  const findMenuOption = useCallback((list, prop, value) => list.find((currentElement) => {
    const { subItems = [] } = currentElement
    if (currentElement[prop] === value) {
      return true
    }
    if (subItems.length) {
      return findMenuOption(subItems, prop, value)
    }
    return false
  }), [])

  const activeMenuItem = useMemo(() => _get(findMenuOption(menuItems, 'path', location.pathname), 'menuItem', ''), [findMenuOption, location.pathname, menuItems])

  if (window.localStorage.getItem('expiredToken') === 'true') {
    sessionExpiredRedirect()
    return null
  }

  return (
    <>
      <StyledAppBar position="fixed">
        <StyledToolbar>
          <Grid container justify="flex-end" alignItems="center">
            <StyledTradeMarkContainer>
              <Grid container justify="flex-start" alignItems="center">
                <StyledTradeMarkBox item onClick={() => history.push('/')} title={t('menu.homepage')}>
                  <Grid container>
                    <Grid item>
                      <LogoGPCNew />
                    </Grid>
                    {/* <Grid item>
                      <LogoGPC />
                    </Grid>
                    <Grid item>
                      <StyledImageContainer>
                        <img src={UnileverTrademark} alt={t('menu.unileverTrademark')} />
                      </StyledImageContainer>
                    </Grid> */}
                  </Grid>
                </StyledTradeMarkBox>
              </Grid>
            </StyledTradeMarkContainer>
            <Grid item>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <CalendarIconNew
                    style={{ color: 'white', cursor: 'pointer', width: '24px', height: '24px' }}
                    onClick={() => handleCalendarOpenChange(!isCalendarOpen)}
                  />
                </Grid>
                <Grid item>
                  <StyledUserName>{store.name}</StyledUserName>
                </Grid>
                <Grid item>
                  <Grid container alignItems="center" spacing={1} onClick={handleUserMenuOpen} style={{ cursor: 'pointer' }}>
                    <Grid item>
                      <UserIconNew style={{ width: '32px', height: '32px', color: 'white' }} />
                    </Grid>
                    <Grid item>
                      <ArrowDownIcon style={{ color: 'white' }} />
                    </Grid>
                  </Grid>
                  <Menu
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleUserMenuClose}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    disableScrollLock
                    disableAutoFocusItem
                    PaperProps={{
                      style: {
                        width: '280px',
                        height: '290px',
                        marginTop: '10px',
                        borderRadius: '10px'
                      }
                    }}
                    MenuListProps={{
                      style: {
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: 0,
                        color: '#060A23'
                      }
                    }}
                  >
                    <MenuItem style={{ flex: 1 }} onClick={() => { history.push('/chat'); handleUserMenuClose() }}>
                      <ListItemIcon style={{ minWidth: '40px' }}><ChatIcon style={{ color: '#060A23' }} /></ListItemIcon>
                      {t('menu.support.chatSupport')}
                    </MenuItem>
                    <MenuItem style={{ flex: 1 }} onClick={() => { openModalChangePassword(); handleUserMenuClose() }}>
                      <ListItemIcon style={{ minWidth: '40px' }}><PasswordIcon style={{ color: '#060A23' }} /></ListItemIcon>
                      {t('menu.support.changeMyPassword')}
                    </MenuItem>
                    <MenuItem style={{ flex: 1 }} onClick={() => { history.push('/user/edition_request'); handleUserMenuClose() }}>
                      <ListItemIcon style={{ minWidth: '40px' }}><UserEditionIcon style={{ color: '#060A23' }} /></ListItemIcon>
                      {t('menu.support.requestUserEdition')}
                    </MenuItem>
                    <MenuItem style={{ flex: 1 }} onClick={handleDiretoryDrive}>
                      <ListItemIcon style={{ minWidth: '40px' }}><FilesIcon style={{ color: '#060A23' }} /></ListItemIcon>
                      {t('menu.support.supportFiles')}
                    </MenuItem>
                    <Divider style={{ margin: '8px 16px', opacity: 0.16, backgroundColor: 'black' }} />
                    <MenuItem style={{ flex: 1 }} onClick={() => { logout(); handleUserMenuClose() }}>
                      <ListItemIcon style={{ minWidth: '40px' }}><ExitIcon style={{ color: '#060A23' }} /></ListItemIcon>
                      {t('menu.out')}
                    </MenuItem>
                  </Menu>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </StyledToolbar>
      </StyledAppBar>
      {!disableMenu && (
        <ClickAwayListener onClickAway={clickAway}>
          <StyledDrawer
            variant="permanent"
            ancor="top"
            open={menuState.opened}
            // onClick={() => setMenuState(menuState => ({ ...menuState, blocked: !menuState.blocked }))}
            onMouseOver={mouseOver}
            onMouseLeave={() => handleToggleMenu(false)}
          >
            <ItemsList
              activeMenuItem={activeMenuItem}
              menuState={menuState}
              setMenuState={setMenuState}
              items={menuItems}
            />
          </StyledDrawer>
        </ClickAwayListener>
      )}
      <Calendar isOpenExternal={isCalendarOpen} setOpenExternal={handleCalendarOpenChange} />
      <ChangePasswordModal
        modalState={modalChangePassword}
        setModalState={setModalChangePassword}
        logout={logout}
      />
      <RequestUserEditionModal
        modalState={modalRequestUserEdition}
        setModalState={setModalRequestUserEdition}
        logout={logout}
        userData={store}
      />
    </>
  )
}

export default memo(MiniDrawer)
